/**
 * Local saves of training sessions (guests, and signed-in players while offline): one entry per `clientId` in
 * localStorage. After signing in they are imported (`importLocalSaves`, POST /api/skill/sessions/import — idempotent by
 * clientId, the newer `version` wins, a finished server copy is kept) and removed locally once the server has them.
 * Contest entries are never local: they are bets and live only on the server.
 */
import { SKILL_GAME_IDS, SKILL_LIMITS, type Locale, type SkillGameId, type SkillParams } from '@tg/shared';
import { isApiRequestError } from '@/api/client';
import { skillApi } from './api';

const RETRY: ReadonlySet<string> = new Set(['NETWORK', 'RATE_LIMITED', 'SERVER_BUSY', 'UNAUTHORIZED', 'INTERNAL']);

const KEY = 'tg.skill.saves.v1';
/** at most this many local saves (the oldest finished ones go first) */
const MAX_SAVES = 12;

export interface LocalSave {
  clientId: string;
  game: SkillGameId;
  locale: Locale;
  seed: string;
  params: SkillParams;
  rulesVersion: number;
  /** +1 on every local write */
  version: number;
  moves: unknown[];
  state?: unknown;
  completed: boolean;
  startedAt: string;
  updatedAt: string;
}

function readAll(): LocalSave[] {
  try {
    const raw = localStorage.getItem(KEY);
    const v: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(v)) return [];
    return v.filter(
      (x): x is LocalSave =>
        !!x && typeof x === 'object' && typeof (x as LocalSave).clientId === 'string' && (SKILL_GAME_IDS as readonly string[]).includes((x as LocalSave).game),
    );
  } catch {
    return [];
  }
}

function writeAll(list: LocalSave[]): boolean {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
    return true;
  } catch {
    return false;
  }
}

export const localSaves = {
  list: (game?: SkillGameId): LocalSave[] => readAll().filter((s) => !game || s.game === game),
  get: (clientId: string): LocalSave | null => readAll().find((s) => s.clientId === clientId) ?? null,
  /** writes the save with version + 1; false when the storage refused it (the caller shows «not saved») */
  put(save: Omit<LocalSave, 'version' | 'updatedAt'> & { version?: number }): LocalSave | null {
    if (save.moves.length > SKILL_LIMITS.maxMoves) return null;
    const list = readAll();
    const prev = list.find((s) => s.clientId === save.clientId);
    const next: LocalSave = { ...save, version: (prev?.version ?? save.version ?? 0) + 1, updatedAt: new Date().toISOString() };
    const rest = list.filter((s) => s.clientId !== save.clientId);
    rest.push(next);
    while (rest.length > MAX_SAVES) {
      const i = rest.findIndex((s) => s.completed);
      rest.splice(i >= 0 ? i : 0, 1);
    }
    return writeAll(rest) ? next : null;
  },
  remove(clientId: string): void {
    writeAll(readAll().filter((s) => s.clientId !== clientId));
  },
};

export function newClientId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  const h = () => Math.floor(Math.random() * 0x10000).toString(16).padStart(4, '0');
  return `${h()}${h()}-${h()}-4${h().slice(1)}-a${h().slice(1)}-${h()}${h()}${h()}`;
}

/** a random training seed (hex) */
export function newSeed(): string {
  const b = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) crypto.getRandomValues(b);
  else for (let i = 0; i < b.length; i++) b[i] = Math.floor(Math.random() * 256);
  return [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
}

/**
 * After signing in: every local save goes to the server. A save the server accepted or already has (created /
 * updated / kept) is removed locally; on a network error it stays for the next attempt. Returns the imported count.
 */
export async function importLocalSaves(): Promise<number> {
  let n = 0;
  for (const s of readAll()) {
    try {
      await skillApi.import({
        game: s.game,
        locale: s.locale,
        clientId: s.clientId,
        seed: s.seed,
        params: s.params,
        rulesVersion: s.rulesVersion,
        version: s.version,
        moves: s.moves,
        state: s.state,
        completed: s.completed,
        startedAt: s.startedAt,
      });
      localSaves.remove(s.clientId);
      n++;
    } catch (e) {
      // a save the server refuses for good (invalid, unknown game rules) would block every later import: drop it;
      // a transient failure keeps it for the next sign-in / app start
      if (isApiRequestError(e) && !RETRY.has(e.code)) localSaves.remove(s.clientId);
    }
  }
  return n;
}
